import {inject, Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {ConsentService} from './consent.service';

type ClarityTag = ((...args: unknown[]) => void) & { q?: IArguments[] };

declare global {
  interface Window {
    clarity?: ClarityTag;
    dataLayer?: IArguments[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Commande passée à la fin de l'onboarding, transmise aux pages de résultat du paiement via le
 * state de navigation.
 */
export interface PurchaseConversion {
  /** Identifiant du PaymentIntent Stripe : sert de `transaction_id`, Google dédoublonne dessus. */
  transactionId: string;
  /** Montant débité à la commande, en unité monétaire (et non en centimes). */
  value: number;
  currency: string;
}

/**
 * Routes dont le chemin d'URL porte un code de vérification ou un email
 * (`public-routing.module.ts`). Clarity et Google Analytics enregistrent l'URL complète de chaque
 * page vue, et le masking de Clarity ne couvre que le DOM : sur ces pages, on ne charge aucun tag.
 */
const TOKEN_BEARING_PATHS = [
  '/modification-mot-de-passe',
  '/completer-le-profil',
  '/verification-email'
];

/**
 * Cookies first-party déposés par les tags. Ils sont portés par le domaine parent, partagé avec le
 * site vitrine (les tags remontent les niveaux de domaine jusqu'à en trouver un qui accepte le
 * cookie). On supprime ceux d'une catégorie non consentie, pour qu'un retrait de consentement sur
 * le site ait un effet ici aussi.
 */
const CLARITY_COOKIES = ['_clck', '_clsk'];
/** `_ga_<ID>` reprend l'identifiant du flux sans son préfixe `G-`. */
const GOOGLE_ANALYTICS_COOKIES = ['_ga', `_ga_${environment.googleTagId.replace(/^G-/, '')}`];
const GOOGLE_ADS_COOKIES = ['_gcl_au', '_gcl_aw', '_gcl_dc'];
const COOKIE_DOMAIN = '.coucouvisio.fr';

/**
 * Durée de vie des cookies Google Analytics : 13 mois, comme sur le site vitrine. `_ga` est
 * partagé avec lui ; sans ce plafond, le tag de l'application le reposerait pour 2 ans.
 */
const GOOGLE_ANALYTICS_COOKIE_EXPIRES = 13 * 30 * 24 * 60 * 60;

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  readonly #consentService = inject(ConsentService);

  /**
   * Démarre chaque tag selon le consentement donné sur le site vitrine. Une catégorie refusée
   * n'injecte rien : pas de script tiers, pas de cookie.
   *
   * Le consentement est évalué au démarrage de l'application. Un utilisateur qui arrive sur une
   * page à token garde les tags éteints jusqu'au prochain chargement complet.
   */
  init(): void {
    this.initClarity();
    this.initGoogleTag();
  }

  /**
   * Remonte à Google Ads la conversion « Achat » d'une commande finalisée, si l'utilisateur a
   * accepté la publicité sur le site vitrine. CCV-18 avait exclu tout consentement faute de
   * catégorie publicitaire dans le bandeau ; elle y est désormais.
   *
   * La balise est d'ordinaire déjà chargée par `init`, sinon elle l'est ici (arrivée par une page à
   * token). L'attribution au clic publicitaire repose sur le cookie `_gcl_aw` posé par la balise
   * Google du site vitrine sur le domaine parent.
   */
  trackPurchaseConversion(conversion: PurchaseConversion): void {
    if (!environment.googleAdsId || !environment.googleAdsPurchaseLabel) {
      return;
    }

    if (!this.#consentService.adsGranted) {
      return;
    }

    this.loadGoogleTag();

    window.gtag!('event', 'conversion', {
      send_to: `${environment.googleAdsId}/${environment.googleAdsPurchaseLabel}`,
      value: conversion.value,
      currency: conversion.currency,
      transaction_id: conversion.transactionId
    });
  }

  /** Microsoft Clarity : mesure d'audience uniquement. */
  private initClarity(): void {
    if (!environment.clarityProjectId) {
      return;
    }

    if (!this.#consentService.analyticsGranted) {
      this.clearCookies(CLARITY_COOKIES);
      return;
    }

    if (this.isTokenBearingUrl()) {
      return;
    }

    this.loadClarity();
  }

  /**
   * Google tag (gtag.js) : sert à la fois Google Analytics (mesure d'audience) et Google Ads
   * (publicité). Il est chargé dès qu'une des deux catégories est acceptée, et Consent Mode n'ouvre
   * que les stockages de la ou des catégories consenties.
   */
  private initGoogleTag(): void {
    if (!environment.googleTagId) {
      return;
    }

    const analyticsGranted = this.#consentService.analyticsGranted;
    const adsGranted = this.#consentService.adsGranted;

    if (!analyticsGranted) {
      this.clearCookies(GOOGLE_ANALYTICS_COOKIES);
    }

    if (!adsGranted) {
      this.clearCookies(GOOGLE_ADS_COOKIES);
    }

    if ((!analyticsGranted && !adsGranted) || this.isTokenBearingUrl()) {
      return;
    }

    this.loadGoogleTag();
  }

  private loadGoogleTag(): void {
    if (window.gtag) {
      return;
    }

    const adsGranted = this.#consentService.adsGranted;
    const analyticsStorage = this.#consentService.analyticsGranted ? 'granted' : 'denied';
    const adStorage = adsGranted ? 'granted' : 'denied';

    // Comme pour Clarity, `dataLayer` bufferise les appels jusqu'à l'arrivée de gtag.js.
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function (): void {
      window.dataLayer!.push(arguments);
    };

    // Même correspondance catégorie du bandeau → signaux Consent Mode que sur le site vitrine.
    window.gtag('consent', 'default', {
      ad_storage: adStorage,
      ad_user_data: adStorage,
      ad_personalization: adStorage,
      analytics_storage: analyticsStorage,
      functionality_storage: analyticsStorage,
      security_storage: 'granted'
    });

    // Une seule bibliothèque gtag.js pour les deux destinations.
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleTagId || environment.googleAdsId}`;
    document.head.appendChild(script);

    window.gtag('js', new Date());

    if (environment.googleTagId) {
      window.gtag('config', environment.googleTagId, {cookie_expires: GOOGLE_ANALYTICS_COOKIE_EXPIRES});
    }

    if (environment.googleAdsId && adsGranted) {
      window.gtag('config', environment.googleAdsId);
    }
  }

  private isTokenBearingUrl(): boolean {
    return TOKEN_BEARING_PATHS.some(path => location.pathname.includes(path));
  }

  private clearCookies(names: string[]): void {
    names.forEach(name => {
      document.cookie = `${name}=; path=/; max-age=0`;
      document.cookie = `${name}=; path=/; max-age=0; domain=${COOKIE_DOMAIN}`;
    });
  }

  private loadClarity(): void {
    // Le stub bufferise les appels émis avant l'arrivée du tag : le tag rejoue `q` au chargement.
    const tag: ClarityTag = function (this: unknown): void {
      (tag.q = tag.q ?? []).push(arguments);
    } as ClarityTag;

    window.clarity = window.clarity ?? tag;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${environment.clarityProjectId}`;
    document.head.appendChild(script);

    // Mesure d'audience acceptée, publicité refusée — la catégorie publicitaire du bandeau ne vaut
    // que pour Google Ads, jamais pour Microsoft.
    window.clarity('consentv2', {ad_Storage: 'denied', analytics_Storage: 'granted'});
  }
}
